import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ArrowLeft, School, User, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useToast } from "@/components/ui/use-toast";
import Header from "../Header";
import AdminSidebar from "../Sidebar";
import { db, auth, storage } from "../../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Define interfaces
interface Affiliation {
  role: string;
  schoolID?: string;
}

// Kenya's 47 counties
const kenyanCounties = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet", "Embu", "Garissa", "Homa Bay", "Isiolo", "Kajiado",
  "Kakamega", "Kericho", "Kiambu", "Kilifi", "Kirinyaga", "Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia",
  "Lamu", "Machakos", "Makueni", "Mandera", "Marsabit", "Meru", "Migori", "Mombasa", "Murang'a", "Nairobi",
  "Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua", "Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River",
  "Tharaka-Nithi", "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot"
];

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  position: z.string().min(2, "Position must be at least 2 characters"),
  phoneNumber: z.string().regex(/^(?:\+254|0)7\d{8}$/, "Please enter a valid Kenyan phone number (e.g., +254712345678 or 0712345678)"),
  schoolName: z.string().min(2, "School name must be at least 2 characters"),
  schoolMotto: z.string().optional(),
  schoolEmail: z.string().email("Please enter a valid email address"),
  schoolAddress: z.string().min(5, "School address must be at least 5 characters"),
  county: z.string().min(1, "Please select a county"),
  schoolSystem: z.enum(["CBC", "8-4-4"], "Please select a school system"),
  schoolType: z.enum(["Boarding School", "Day School", "Mixed (Day & Boarding)"], "Please select a school type"),
  accountNumber: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface EditSchoolDetailsProps {
  onBack: () => void;
}

const EditSchoolDetails = ({ onBack }: EditSchoolDetailsProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uid, setUid] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSchool, setHasSchool] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string>("");
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      position: "",
      phoneNumber: "",
      schoolName: "",
      schoolMotto: "",
      schoolEmail: "",
      schoolAddress: "",
      county: "",
      schoolSystem: undefined,
      schoolType: undefined,
      accountNumber: "",
    },
  });

  // Load existing school data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUid(currentUser.uid);
        try {
          // Get schoolID from claims
          await currentUser.getIdToken(true);
          const idTokenResult = await currentUser.getIdTokenResult(true);
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

          let selectedAffiliation: Affiliation | null = null;
          if (affiliations.length > 0) {
            if (defaultAffiliation && defaultAffiliation.role === "admin") {
              selectedAffiliation = defaultAffiliation;
            } else {
              selectedAffiliation = affiliations.find(aff => aff.role === "admin") || defaultAffiliation || affiliations[0];
            }
          }

          if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID) {
            const selectedSchoolID = selectedAffiliation.schoolID;
            setSchoolID(selectedSchoolID);

            // Load school details
            const schoolDetailsDoc = await getDoc(doc(db, "schools", selectedSchoolID, "schoolDetails", "main"));
            if (schoolDetailsDoc.exists()) {
              const schoolData = schoolDetailsDoc.data();
              setHasSchool(true);

              // Set existing logo if available
              if (schoolData.logoUrl) {
                setExistingLogoUrl(schoolData.logoUrl);
                setLogoPreview(schoolData.logoUrl);
              }

              // Load user teaching details
              const teachingDoc = await getDoc(doc(db, "users", currentUser.uid, "schoolteaching", selectedSchoolID));
              const teachingData = teachingDoc.exists() ? teachingDoc.data() : {};

              // Load user personal data
              const userDoc = await getDoc(doc(db, "users", currentUser.uid));
              const userData = userDoc.exists() ? userDoc.data() : {};

              // Pre-fill form with existing data
              form.setValue("schoolName", schoolData.name || "");
              form.setValue("schoolMotto", schoolData.motto || "");
              form.setValue("schoolEmail", schoolData.email || "");
              form.setValue("schoolAddress", schoolData.address || "");
              form.setValue("county", schoolData.county || "");
              form.setValue("schoolSystem", schoolData.system || "CBC");
              form.setValue("schoolType", schoolData.type || "Day School");
              form.setValue("position", teachingData.position || "");
              form.setValue("phoneNumber", teachingData.phoneNumber || "");
              form.setValue("firstName", userData.firstName || "");
              form.setValue("lastName", userData.lastName || "");

              // Handle Account Number
              if (schoolData.accountNumber) {
                form.setValue("accountNumber", schoolData.accountNumber);
              } else if (schoolData.name && selectedSchoolID) {
                // Generate if missing
                const cleanName = schoolData.name.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                const uniqueSuffix = selectedSchoolID.slice(-4).toUpperCase();
                const generatedAcc = `${cleanName.substring(0, 8)}${uniqueSuffix}`;
                form.setValue("accountNumber", generatedAcc);
              }
            } else {
              setHasSchool(false);
              toast({
                variant: "destructive",
                title: "No School Found",
                description: "No school details found. Please register your school first.",
              });
            }
          } else {
            setSchoolID(null);
            toast({
              variant: "destructive",
              title: "Error",
              description: "No valid school ID found in user affiliation.",
            });
          }

          setIsLoading(false);
        } catch (err: any) {
          console.error("Error loading school data:", err);
          toast({
            variant: "destructive",
            title: "Error",
            description: `Failed to load school data: ${err.message || "Unknown error"}`,
          });
          setIsLoading(false);
        }
      } else {
        setUid(null);
        setSchoolID(null);
        setIsLoading(false);
        toast({
          variant: "destructive",
          title: "Error",
          description: "No user logged in. Please log in and try again.",
        });
      }
    });

    return () => unsubscribe();
  }, [form, toast]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          variant: "destructive",
          title: "Invalid file type",
          description: "Please upload an image file",
        });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: "File too large",
          description: "Logo must be less than 5MB",
        });
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(existingLogoUrl || null);
  };

  const onSubmit = async (data: FormData) => {
    if (!uid || !schoolID) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "User or school information missing.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload new logo if provided
      let logoUrl = existingLogoUrl;
      if (logoFile) {
        const logoRef = ref(storage, `schools/${schoolID}/logos/logo_${logoFile.name}`);
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
      }

      // Update users/{uid}/schoolteaching/{schoolID}
      await setDoc(doc(db, "users", uid, "schoolteaching", schoolID), {
        position: data.position,
        phoneNumber: data.phoneNumber,
        updatedAt: new Date(),
      }, { merge: true });

      // Update schools/{schoolID}/schoolDetails/main
      await setDoc(doc(db, "schools", schoolID, "schoolDetails", "main"), {
        name: data.schoolName,
        motto: data.schoolMotto,
        email: data.schoolEmail,
        address: data.schoolAddress,
        system: data.schoolSystem,
        type: data.schoolType,
        county: data.county,
        logoUrl: logoUrl || "",
        accountNumber: data.accountNumber,
        updatedAt: new Date(),
        updatedBy: uid,
      }, { merge: true });

      // Update users/{uid}
      await setDoc(doc(db, "users", uid), {
        firstName: data.firstName,
        lastName: data.lastName,
        updatedAt: new Date(),
      }, { merge: true });

      toast({
        title: "Success",
        description: "School details updated successfully!",
      });
      onBack();
    } catch (error: any) {
      console.error("Error updating school details:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update school details: ${error.message || "Unknown error"}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 overflow-y-auto p-3 sm:p-6">
              <div className="max-w-2xl mx-auto flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">Loading school details...</p>
                </div>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (!hasSchool) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 overflow-y-auto p-3 sm:p-6">
              <div className="max-w-2xl mx-auto">
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-12 text-center">
                    <School className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">No School Found</h2>
                    <p className="text-gray-600 mb-4">
                      No school details found. Please register your school first.
                    </p>
                    <Button onClick={onBack} variant="outline">
                      Back to Settings
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
          <main className="flex-1 overflow-y-auto p-3 sm:p-6">
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <Button
                  variant="ghost"
                  onClick={onBack}
                  className="mb-4 hover:bg-gray-100"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Settings
                </Button>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <School className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Edit School Details</h1>
                    <p className="text-gray-600">Update your school information</p>
                  </div>
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>School Information</CardTitle>
                  <CardDescription>
                    Review and update your school details below
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-3">
                          <User className="h-4 w-4 text-blue-600" />
                          <h3 className="font-semibold text-gray-800">Personal Information</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>First Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Enter your first name" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Last Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Enter your last name" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <FormField
                          control={form.control}
                          name="position"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Capacity / Position in School</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g., Headteacher, Principal, Administrator" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phoneNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone Number</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g., +254712345678 or 0712345678" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-3">
                          <School className="h-4 w-4 text-blue-600" />
                          <h3 className="font-semibold text-gray-800">School Information</h3>
                        </div>
                        <FormField
                          control={form.control}
                          name="schoolName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>School Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter your school name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="accountNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Account Number (Auto-assigned)</FormLabel>
                              <FormControl>
                                <Input {...field} readOnly className="bg-gray-100 text-gray-500 cursor-not-allowed" />
                              </FormControl>
                              <p className="text-xs text-gray-500">Unique identifier used for M-Pesa payments.</p>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="schoolMotto"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>School Motto (Optional)</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter your school motto" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        {/* Logo Upload */}
                        <div className="space-y-2">
                          <FormLabel>School Logo (Optional)</FormLabel>
                          <div className="flex items-center gap-4">
                            {logoPreview ? (
                              <div className="relative">
                                <img
                                  src={logoPreview}
                                  alt="School logo preview"
                                  className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                                  onClick={removeLogo}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors bg-transparent">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                  <p className="text-sm text-gray-500">Click to upload logo</p>
                                  <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                                </div>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/*"
                                  onChange={handleLogoChange}
                                />
                              </label>
                            )}
                          </div>
                        </div>

                        <FormField
                          control={form.control}
                          name="schoolEmail"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>School Email</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="school@example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="schoolAddress"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>School Address</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter complete school address" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="county"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>County</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select county" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-48">
                                    {kenyanCounties.map((county) => (
                                      <SelectItem key={county} value={county}>
                                        {county}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="schoolSystem"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>School System</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select school system" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="CBC">CBC (Competency Based Curriculum)</SelectItem>
                                    <SelectItem value="8-4-4">8-4-4 System</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <FormField
                          control={form.control}
                          name="schoolType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>School Type</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select school type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="Boarding School">Boarding School</SelectItem>
                                  <SelectItem value="Day School">Day School</SelectItem>
                                  <SelectItem value="Mixed (Day & Boarding)">Mixed (Day & Boarding)</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={onBack}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                          {isSubmitting ? "Saving..." : "Save Changes"}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default EditSchoolDetails;
